/**
 * InterviewSetup — configure respondent and objectives before starting.
 * Route: /coordinator/clients/:clientId/interviews/new (create)
 *     or /coordinator/clients/:clientId/interviews/:interviewId/setup (edit)
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import ClientBreadcrumb from "../../components/ClientBreadcrumb.jsx";
import SendInterviewDialog from "../../components/coordinator/SendInterviewDialog.jsx";
import { contributesWithoutEditing } from "../../lib/roles.js";

// A saved respondent role, back to the relationship it most likely came from,
// for re-editing a draft (Oct 28).
const RELATIONSHIP_FOR_ROLE = { parent: "family_parent", family: "family_other",
                                caregiver: "caregiver", coordinator: "coordinator" };

// Objective starter library. `goal` is the required intent (what PE translates
// into a warm question — never a respondent-facing question itself); `background`
// is optional grounding. Coordinators drop one in and edit.
const OBJECTIVE_LIBRARY = [
  { label: "Daily routine",
    goal: "Understand what a typical day looks like for this person, from waking to bedtime, and where the rhythm matters most.",
    background: "Focus on the transitions and moments that tend to go well or poorly." },
  { label: "Communication",
    goal: "Learn how this person expresses needs, wants, yes/no, and distress — including any non-verbal or personal signals a caregiver should recognize.",
    background: "" },
  { label: "What soothes them",
    goal: "Understand what reliably calms or comforts this person when they're upset or overwhelmed.",
    background: "Include things that have worked in the past, even if they seem unusual." },
  { label: "What sets them off",
    goal: "Learn what tends to cause distress, frustration, or escalation for this person, and any early warning signs.",
    background: "The aim is to help a caregiver avoid or de-escalate, not to dwell on the negative." },
  { label: "Sensory preferences",
    goal: "Understand this person's sensory likes and dislikes — sounds, textures, lights, foods, touch — that shape their comfort.",
    background: "" },
  { label: "Key relationships",
    goal: "Learn who the most important people are in this person's life and what role each plays.",
    background: "Include family, friends, housemates, and staff they're closest to." },
  { label: "What brings them joy",
    goal: "Understand what this person loves, looks forward to, and finds genuinely fun or meaningful.",
    background: "" },
  { label: "Health & medical must-knows",
    goal: "Learn the health and medical facts a caregiver must know to keep this person safe day to day.",
    background: "Focus on practical must-knows, not a full medical history." },
  { label: "Food & mealtimes",
    goal: "Understand this person's food preferences, aversions, and anything important about how mealtimes go.",
    background: "" },
  { label: "Independence & support",
    goal: "Learn what this person can do on their own and where they want or need support, so caregivers neither over- nor under-help.",
    background: "The goal is respecting autonomy while staying safe." },
  { label: "Skills & abilities",
    goal: "Understand this person's particular skills, talents, and things they're good at or proud of.",
    background: "Include everyday competencies and any special interests they've developed real ability in." },
];

function ObjectiveRow({ obj, index, onChange, onRemove }) {
  // Show the optional context field when it already has content (e.g. from a
  // library template or a previously-saved objective), otherwise keep it
  // collapsed behind a toggle.
  const [showContext, setShowContext] = useState(!!(obj.background && obj.background.trim()));

  return (
    <div style={s.objRow}>
      <div style={s.objNum}>{index + 1}</div>
      <div style={s.objFields}>
        <label style={s.label}>
          What do you want to learn about this person?
          <span style={s.hint}> (your intent — we'll turn it into a warm question)</span>
          <textarea style={s.textarea} rows={2} value={obj.goal}
            onChange={e => onChange({ ...obj, goal: e.target.value })}
            placeholder="e.g. Understand what Maris's personal number system means to her and how caregivers should engage with it" />
        </label>

        {showContext ? (
          <label style={s.label}>
            Any context that would help ask this well?
            <span style={s.hint}> (optional)</span>
            <textarea style={s.textarea} rows={2} value={obj.background}
              onChange={e => onChange({ ...obj, background: e.target.value })}
              placeholder="e.g. Maris uses a personal number system daily, but we don't fully understand how it works" />
          </label>
        ) : (
          <button type="button" style={s.addContextBtn} onClick={() => setShowContext(true)}>
            + Add context for the AI (optional)
          </button>
        )}
      </div>
      <button style={s.removeBtn} onClick={onRemove} title="Remove objective">×</button>
    </div>
  );
}

export default function InterviewSetup() {
  const { clientId, interviewId } = useParams();
  const navigate  = useNavigate();
  const { user, can } = useAuth();
  const isNew     = !interviewId;
  // A contributor (Oct 24) sets up an interview only for herself: no "who is
  // answering", no sending. The server enforces this whatever the page sends;
  // the page just doesn't offer what she can't do.
  // Decided per client from the server's viewer_capabilities once loaded;
  // until then, from the account-level capability.
  const [caps,     setCaps]     = useState(null);
  const forSelf   = caps ? contributesWithoutEditing(caps) : !can("build_profiles");
  const hub       = `/coordinator/clients/${clientId}/interviews`;

  const [clientName,   setClientName]   = useState("");
  const [relationship, setRelationship] = useState("");
  const [relationships, setRelationships] = useState([]);
  const [displayName,  setDisplayName]  = useState("");
  const [context,      setContext]      = useState("");
  // Who answers is chosen when it's sent (Oct 28). Set-up only names someone
  // answering in person, here with the coordinator.
  const [sending,      setSending]      = useState(null);   // saved interview awaiting the Send dialog
  const [objectives,   setObjectives]   = useState([{ background: "", goal: "" }]);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState(null);

  useEffect(() => {
    fetch(`/api/clients/${clientId}`).then(r => r.json())
      .then(d => { setClientName(d.display_name || ""); setCaps(d.viewer_capabilities || null); })
      .catch(() => {});
    if (!forSelf) {
      fetch(`/api/clients/${clientId}/interviews/relationships`).then(r => r.ok ? r.json() : [])
        .then(list => setRelationships(Array.isArray(list) ? list : []))
        .catch(() => setRelationships([]));
    }

    if (!isNew) {
      fetch(`/api/clients/${clientId}/interviews/${interviewId}`).then(r => r.json())
        .then(iv => {
          if (iv.respondent) {
            setRelationship(RELATIONSHIP_FOR_ROLE[iv.respondent.role] || "");
            setDisplayName(iv.respondent.display_name || "");
            setContext(iv.respondent.context || "");
          }
          if (iv.objectives?.length) {
            setObjectives(iv.objectives.map(o => ({ background: o.background, goal: o.goal })));
          }
        }).catch(() => {});
    }
  }, [clientId, interviewId, isNew, forSelf]);

  function addObjective() {
    setObjectives(prev => [...prev, { background: "", goal: "" }]);
  }

  function addFromLibrary(template) {
    setObjectives(prev => {
      // If the only objective is empty, replace it; otherwise append.
      const onlyEmpty = prev.length === 1 && !prev[0].goal.trim() && !prev[0].background.trim();
      const next = { background: template.background || "", goal: template.goal };
      return onlyEmpty ? [next] : [...prev, next];
    });
  }

  // after: "start" opens the session here, "send" opens the Send dialog,
  // "later" returns to the hub with the interview left in draft (Plan E3).
  async function handleSave(after = "start") {
    // goal is required; background is optional (per PE: goal is load-bearing).
    const validObjs = objectives
      .filter(o => o.goal.trim())
      .map(o => ({ goal: o.goal.trim(), background: o.background.trim() }));
    if (!validObjs.length) { setError("Add at least one objective — tell us what you want to learn."); return; }
    setSaving(true);
    setError(null);

    const body = forSelf ? { objectives: validObjs } : {
      respondent: {
        ...(relationship ? { relationship } : {}),
        display_name: displayName.trim() || null,
        context: context.trim() || null,
      },
      objectives: validObjs,
    };

    try {
      let res, iv;
      if (isNew) {
        res = await fetch(`/api/clients/${clientId}/interviews`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`/api/clients/${clientId}/interviews/${interviewId}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
      }
      iv = await res.json();
      if (!res.ok) { setError(iv.error || "Save failed"); return; }
      if (after === "start")      navigate(`/coordinator/clients/${clientId}/interviews/${iv.id}/session`);
      else if (after === "send")  setSending(iv);
      else                        navigate(`/coordinator/clients/${clientId}/edit`);
    } catch (e) { setError("Save failed: " + e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={s.page}>
      <div style={s.content}>
        <ClientBreadcrumb
          clientName={clientName}
          role={user?.role}
          clientId={clientId}
          trail={[
            { label: "Interviews", href: hub },
            { label: isNew ? "Set up" : "Edit" },
          ]}
        />
        <h1 style={s.title}>{forSelf ? "Start an interview" : isNew ? "Set up interview" : "Edit interview"}</h1>
        <p style={s.subtitle}>
          {forSelf
            ? `Choose what you'd like to talk about, and you'll answer a few questions about ${clientName || "them"} in your own words. The coordinator reviews what you say before it goes on the profile.`
            : "Configure the respondent and the objectives — what you want to learn from this interview."}
        </p>

        {error && <p style={s.error}>{error}</p>}

        {/* Respondent — a contributor is always answering herself. */}
        {!forSelf && <section style={s.section}>
          <h2 style={s.sectionTitle}>Answering in person?</h2>
          <p style={s.hint}>
            To send it to someone, choose who when you send it. If you'll go through it together
            here, say who they are.
          </p>
          <div style={s.row}>
            <label style={{ ...s.label, flex: 1 }}>
              Who they are to {clientName || "them"} <span style={s.hint}>(optional)</span>
              <select style={s.select} value={relationship} onChange={e => setRelationship(e.target.value)}
                aria-label="Relationship">
                <option value="">—</option>
                {relationships.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            <label style={{ ...s.label, flex: 2 }}>
              Name <span style={s.hint}>(optional)</span>
              <input style={s.input} value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="e.g. Dawn" />
            </label>
          </div>
          <label style={s.label}>
            Additional context about this respondent <span style={s.hint}>(optional)</span>
            <input style={s.input} value={context}
              onChange={e => setContext(e.target.value)}
              placeholder="e.g. Has cared for Maris for 3 years, knows her routines well" />
          </label>
        </section>}

        {/* Objectives */}
        <section style={s.section}>
          <div style={s.objHeader}>
            <h2 style={s.sectionTitle}>{forSelf ? "What would you like to talk about?" : "What do you want to learn?"}</h2>
            <p style={s.hint}>Each item becomes one part of the conversation. Add your own, or start from a common topic below.</p>
          </div>

          <div style={s.libraryRow}>
            {OBJECTIVE_LIBRARY.map(t => (
              <button key={t.label} type="button" style={s.libraryChip}
                onClick={() => addFromLibrary(t)} title={t.goal}>
                + {t.label}
              </button>
            ))}
          </div>

          <div style={s.objList}>
            {objectives.map((obj, i) => (
              <ObjectiveRow key={i} obj={obj} index={i}
                onChange={updated => setObjectives(prev => prev.map((o, idx) => idx === i ? updated : o))}
                onRemove={() => setObjectives(prev => prev.filter((_, idx) => idx !== i))} />
            ))}
          </div>

          <button style={s.addObjBtn} onClick={addObjective}>+ Add a blank one</button>
        </section>

        <div style={s.footer}>
          <button style={s.cancelBtn}
            onClick={() => navigate(forSelf ? hub : `/coordinator/clients/${clientId}/edit`)}>
            Cancel
          </button>
          <span style={{ flex: 1 }} />
          {!forSelf && <>
          <button style={{ ...s.cancelBtn, ...(saving ? s.muted : {}) }}
            onClick={() => handleSave("later")} disabled={saving} title="Keep it as a draft and come back to it">
            Save for later
          </button>
          <button style={{ ...s.cancelBtn, ...s.sendBtn, ...(saving ? s.muted : {}) }}
            onClick={() => handleSave("send")} disabled={saving} title="They answer when they have ten minutes">
            Send to someone →
          </button>
          </>}
          <button style={{ ...s.startBtn, ...(saving ? s.muted : {}) }}
            onClick={() => handleSave("start")} disabled={saving} title="Ask the questions now, in the room">
            {saving ? "Saving…" : "Start interview →"}
          </button>
        </div>
      </div>
      {sending && (
        <SendInterviewDialog clientId={clientId} clientName={clientName} interview={sending}
                             onClose={() => navigate(`/coordinator/clients/${clientId}/edit`)} />
      )}
    </div>
  );
}

const s = {
  page:        { minHeight: "100dvh", background: "var(--color-bg-subtle)" },
  content:     { maxWidth: 720, margin: "0 auto", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-6)" },
  title:       { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-bold)", color: "var(--color-text-primary)" },
  subtitle:    { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)" },
  error:       { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
  section:     { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  sectionTitle:{ fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  row:         { display: "flex", gap: "var(--space-3)", flexWrap: "wrap" },
  label:       { display: "flex", flexDirection: "column", gap: "var(--space-1)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  hint:        { fontSize: 11, color: "var(--color-text-tertiary)", fontWeight: "normal" },
  input:       { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)" },
  select:      { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)" },
  textarea:    { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", fontFamily: "inherit", resize: "vertical" },
  objHeader:   { display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  objList:     { display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  objRow:      { display: "flex", gap: "var(--space-3)", alignItems: "flex-start" },
  objNum:      { width: 24, height: 24, borderRadius: "var(--radius-full)", background: "var(--color-accent-subtle)", color: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: "var(--weight-semibold)", flexShrink: 0, marginTop: 4 },
  objFields:   { flex: 1, display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  removeBtn:   { background: "none", border: "none", color: "var(--color-text-tertiary)", cursor: "pointer", fontSize: 18, padding: 0, marginTop: 4 },
  addObjBtn:   { fontSize: "var(--text-sm)", color: "var(--color-accent)", background: "none", border: "1px dashed var(--color-accent)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer", alignSelf: "flex-start" },
  libraryRow:  { display: "flex", flexWrap: "wrap", gap: "var(--space-2)", marginBottom: "var(--space-3)" },
  libraryChip: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "var(--color-bg-subtle)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-full)", padding: "4px var(--space-3)", cursor: "pointer", whiteSpace: "nowrap" },
  addContextBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", background: "none", border: "none", padding: "2px 0", cursor: "pointer", textAlign: "left", textDecoration: "underline" },
  footer:      { display: "flex", justifyContent: "flex-end", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "center", paddingTop: "var(--space-4)", borderTop: "1px solid var(--color-border)" },
  cancelBtn:   { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  sendBtn:     { color: "var(--color-accent)", borderColor: "var(--color-accent)" },
  startBtn:    { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  muted:       { opacity: 0.5, cursor: "not-allowed" },
};
